/**
 * The capabilities the assistant will admit to having, in the shape the backend's
 * `registry.menu()` returns: `{id, label, description}`.
 *
 * Held here rather than fetched because the list is four entries long and the ids are the
 * registry's own — the backend revalidates whichever one is sent against the registry and
 * re-applies the permission clamp, so a stale entry here is refused, not obeyed. Swap this for
 * the router's status endpoint the moment the list stops being this short.
 *
 * They are shown as chips on first open rather than as tabs. §8 open decision 3: tabs impose
 * the routing burden on the user on EVERY turn to fix a problem that occurs only on ambiguous
 * ones — and once tabs exist, the single intelligent assistant is gone.
 */
export interface AssistantCapability {
  /** A registered capability id, sent as `options.capabilityId`. */
  id: string
  label: string
  description: string
}

type Translate = (id: string) => string

type AssistantCapabilityGates = {
  /** Attendance board available (hostname/tenant gate). */
  attendanceEnabled: boolean
  /** Knowledge-base board available (`knowledge_base` permission). */
  faqEnabled: boolean
  /** The selective AI domains' help-only assistant: how-to answers from its manual, no data. */
  aiHelp?: boolean
}

/**
 * The capability list for this user, in the order it reads best: data first, then the school's
 * own information, then how-to.
 */
export function getAssistantCapabilities(
  t: Translate,
  {attendanceEnabled, faqEnabled, aiHelp = false}: AssistantCapabilityGates
): AssistantCapability[] {
  // The help bot does one thing (`support.help`; node-api locks every turn there into the help
  // scope anyway), and its welcome chips — the manual's sections for this screen — already offer
  // it. A card of its own would send its label as the question, and the manual has no answer for
  // "How-to answers".
  if (aiHelp) return []

  const capabilities: AssistantCapability[] = []

  if (attendanceEnabled) {
    capabilities.push({
      id: 'attendance.data',
      label: t('CHATBOT.CAPABILITY.ATTENDANCE_DATA_LABEL'),
      description: t('CHATBOT.CAPABILITY.ATTENDANCE_DATA_DESCRIPTION'),
    })
  }

  // The school's own notes (General Settings → Versi Knowledge Base), for every role. Whether this
  // school wrote any for this user's role can't be known here — the setting never reaches a
  // student, parent or teacher — so the chip is always offered; without notes Versi replies that
  // the school hasn't added this information yet.
  capabilities.push({
    id: 'school.knowledge',
    label: t('CHATBOT.CAPABILITY.SCHOOL_KNOWLEDGE_LABEL'),
    description: t('CHATBOT.CAPABILITY.SCHOOL_KNOWLEDGE_DESCRIPTION'),
  })

  if (faqEnabled) {
    capabilities.push(
      {
        id: 'support.manual',
        label: t('CHATBOT.CAPABILITY.SUPPORT_MANUAL_LABEL'),
        description: t('CHATBOT.CAPABILITY.SUPPORT_MANUAL_DESCRIPTION'),
      },
      {
        id: 'support.walkthrough',
        label: t('CHATBOT.CAPABILITY.SUPPORT_WALKTHROUGH_LABEL'),
        description: t('CHATBOT.CAPABILITY.SUPPORT_WALKTHROUGH_DESCRIPTION'),
      }
    )
  }

  return capabilities
}
